import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, Navigation } from 'swiper/modules'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'

interface ProductImageCarouselProps {
  images?: Array<{
    id: string
    image_url: string
    alt_text?: string | null
    is_primary?: boolean
    sort_order?: number
  }>
  productName: string
  fallbackImage?: string
}

export default function ProductImageCarousel({
  images = [],
  productName,
  fallbackImage
}: ProductImageCarouselProps) {
  // Sort images by sort_order and put primary first
  const sortedImages = [...images].sort((a, b) => {
    if (a.is_primary && !b.is_primary) return -1
    if (!a.is_primary && b.is_primary) return 1
    return (a.sort_order || 0) - (b.sort_order || 0)
  })

  // If no images, show fallback
  if (sortedImages.length === 0) {
    return (
      <div className="w-full aspect-square bg-gray-100 rounded-xl flex items-center justify-center">
        {fallbackImage ? (
          <img
            src={fallbackImage}
            alt={productName}
            className="w-full h-full object-cover rounded-xl"
          />
        ) : (
          <div className="text-6xl">📦</div>
        )}
      </div>
    )
  }

  // If only one image, don't show carousel
  if (sortedImages.length === 1) {
    return (
      <div className="w-full aspect-square bg-gray-100 rounded-xl overflow-hidden">
        <img
          src={sortedImages[0].image_url}
          alt={sortedImages[0].alt_text || productName}
          className="w-full h-full object-cover"
        />
      </div>
    )
  }

  // Multiple images - show carousel
  return (
    <div className="w-full">
      <style>{`
        .swiper-pagination-bullet {
          background-color: rgba(156, 163, 175, 0.5);
          opacity: 1;
          width: 8px;
          height: 8px;
        }
        .swiper-pagination-bullet-active {
          background-color: rgb(59, 130, 246);
          width: 24px;
          border-radius: 4px;
        }
        .swiper-button-prev,
        .swiper-button-next {
          color: rgb(59, 130, 246);
          background: rgba(255, 255, 255, 0.9);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          backdrop-filter: blur(4px);
        }
        .swiper-button-prev:after,
        .swiper-button-next:after {
          font-size: 16px;
          font-weight: bold;
        }
        @media (max-width: 640px) {
          .swiper-button-prev,
          .swiper-button-next {
            display: none;
          }
        }
      `}</style>

      <Swiper
        modules={[Pagination, Navigation]}
        spaceBetween={10}
        slidesPerView={1}
        pagination={{
          clickable: true,
          dynamicBullets: true
        }}
        navigation={true}
        loop={sortedImages.length > 2}
        className="w-full aspect-square rounded-xl overflow-hidden bg-gray-100"
      >
        {sortedImages.map((image, index) => (
          <SwiperSlide key={image.id || index}>
            <div className="w-full h-full relative">
              <img
                src={image.image_url}
                alt={image.alt_text || `${productName} - Image ${index + 1}`}
                className="w-full h-full object-cover"
                loading={index === 0 ? 'eager' : 'lazy'}
              />
              {sortedImages.length > 1 && (
                <div className="absolute bottom-4 right-4 bg-black/50 text-white px-2 py-1 rounded-full text-xs">
                  {index + 1} / {sortedImages.length}
                </div>
              )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}